import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, FlatList, StyleSheet, TouchableOpacity, Alert} from 'react-native';
import {
  Text, Button, Appbar, Card, Chip,
  Searchbar, Portal, Modal, RadioButton, Divider,
} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useCustomerStore} from '../../store/useCustomerStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';

const OrderCreateScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {customers, fetchCustomers, searchCustomers} = useCustomerStore();
  const {orgUnits, selectedOrgUnitId, fetchOrgUnits} = useOrgUnitStore();

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [storeId, setStoreId] = useState(selectedOrgUnitId);
  const [storeModalVisible, setStoreModalVisible] = useState(false);

  useEffect(() => {
    fetchCustomers({page: 0, size: 50});
    if (orgUnits.length === 0) {
      fetchOrgUnits();
    }
  }, [fetchCustomers, orgUnits.length, fetchOrgUnits]);

  // Auto-select if only one store exists
  useEffect(() => {
    if (orgUnits.length === 1 && !storeId) {
      setStoreId(orgUnits[0].id);
    }
  }, [orgUnits, storeId]);

  // Handle return from CustomerForm with newly created customer
  useEffect(() => {
    if (route.params?.newCustomer) {
      setSelectedCustomer(route.params.newCustomer);
      setCustomerSearch('');
      // Clear the param so it doesn't re-trigger
      navigation.setParams({newCustomer: undefined});
    }
  }, [route.params?.newCustomer, navigation]);

  const handleCustomerSearch = useCallback(query => {
    setCustomerSearch(query);
    if (query.trim().length > 0) {
      searchCustomers(query.trim());
    } else {
      fetchCustomers({page: 0, size: 50});
    }
  }, [searchCustomers, fetchCustomers]);

  const handleAddCustomer = useCallback(() => {
    const query = customerSearch.trim();
    // Detect if search text looks like a phone number
    const isPhone = /^\d{5,}$/.test(query);
    navigation.navigate('CustomerForm', {
      returnTo: 'OrderCreate',
      prefillPhone: isPhone ? query : '',
      prefillName: isPhone ? '' : query,
    });
  }, [customerSearch, navigation]);

  const handleNext = useCallback(() => {
    if (!storeId) {
      Alert.alert(t('common.error'), 'Please select a store');
      return;
    }
    if (!selectedCustomer) {
      Alert.alert(t('common.error'), 'Please select a customer');
      return;
    }
    navigation.navigate('SelectItem', {
      orgUnitId: storeId,
      customerId: selectedCustomer.id,
      customerName: selectedCustomer.fullName,
      customerGender: selectedCustomer.gender,
      orderId: null,
      existingLines: [],
    });
  }, [storeId, selectedCustomer, navigation, t]);

  const displayedCustomers = customers.slice(0, 10);
  const hasSearchQuery = customerSearch.trim().length > 0;
  const noResults = hasSearchQuery && displayedCustomers.length === 0;

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={t('order.createOrder')} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        {orgUnits.length > 1 && (
          <>
            <Text variant="titleMedium" style={styles.stepTitle}>{t('order.selectStore')}</Text>
            {orgUnits.length <= 4 ? (
              <View style={styles.storeRow}>
                {orgUnits.map(store => (
                  <Chip
                    key={store.id}
                    selected={storeId === store.id}
                    showSelectedCheck={storeId === store.id}
                    selectedColor={storeId === store.id ? colors.white : undefined}
                    onPress={() => setStoreId(store.id)}
                    style={[styles.storeChip, storeId === store.id && styles.storeChipActive]}
                    textStyle={storeId === store.id ? styles.storeChipTextActive : styles.storeChipText}>
                    <Text style={{fontSize: 13, color: storeId === store.id ? '#fff' : '#000', marginTop: 0}}>{store.name}</Text>
                  </Chip>
                ))}
              </View>
            ) : (
              <>
                <TouchableOpacity
                  style={styles.storeDropdown}
                  onPress={() => setStoreModalVisible(true)}
                  activeOpacity={0.7}>
                  <MaterialCommunityIcons name="store-outline" size={20} color={storeId ? colors.primary : colors.textSecondary} />
                  <Text style={[styles.storeDropdownText, storeId && styles.storeDropdownTextSelected]} numberOfLines={1}>
                    {storeId ? orgUnits.find(s => s.id === storeId)?.name : t('order.selectStore')}
                  </Text>
                  <MaterialCommunityIcons name="chevron-down" size={20} color={colors.textSecondary} />
                </TouchableOpacity>
                <Portal>
                  <Modal
                    visible={storeModalVisible}
                    onDismiss={() => setStoreModalVisible(false)}
                    contentContainerStyle={styles.storeModal}>
                    <Text style={styles.storeModalTitle}>{t('order.selectStore')}</Text>
                    <Divider />
                    <FlatList
                      data={orgUnits}
                      keyExtractor={item => String(item.id)}
                      renderItem={({item}) => (
                        <TouchableOpacity
                          style={styles.storeOption}
                          onPress={() => { setStoreId(item.id); setStoreModalVisible(false); }}>
                          <RadioButton.Android
                            value={String(item.id)}
                            status={storeId === item.id ? 'checked' : 'unchecked'}
                            onPress={() => { setStoreId(item.id); setStoreModalVisible(false); }}
                            color={colors.primary}
                          />
                          <View style={styles.storeOptionContent}>
                            <Text style={styles.storeOptionName}>{item.name}</Text>
                            {item.city && <Text style={styles.storeOptionCity}>{item.city}</Text>}
                          </View>
                        </TouchableOpacity>
                      )}
                    />
                  </Modal>
                </Portal>
              </>
            )}
          </>
        )}

        <Text variant="titleMedium" style={styles.stepTitle}>{t('order.selectCustomer')}</Text>
        {selectedCustomer ? (
          <Card style={styles.selectedCard}>
            <Card.Content style={styles.selectedRow}>
              <View>
                <Text variant="titleMedium">{selectedCustomer.fullName}</Text>
                <Text variant="bodySmall" style={styles.label}>{selectedCustomer.phone}</Text>
              </View>
              <Button mode="text" onPress={() => setSelectedCustomer(null)}>Change</Button>
            </Card.Content>
          </Card>
        ) : (
          <>
            <Searchbar
              placeholder={t('customer.searchPlaceholder')}
              value={customerSearch}
              onChangeText={handleCustomerSearch}
              style={styles.searchbar}
            />
            {noResults ? (
              <View style={styles.noResultsContainer}>
                <MaterialCommunityIcons name="account-search-outline" size={48} color={colors.textLight} />
                <Text style={styles.noResultsText}>
                  No customers found for "{customerSearch.trim()}"
                </Text>
                <Button
                  mode="contained"
                  icon="account-plus"
                  onPress={handleAddCustomer}
                  style={styles.addCustomerButton}
                  buttonColor={colors.primary}>
                  Add New Customer
                </Button>
              </View>
            ) : (
              <>
                {displayedCustomers.map(c => (
                  <TouchableOpacity key={c.id} onPress={() => setSelectedCustomer(c)}>
                    <Card style={styles.listCard}>
                      <Card.Content style={styles.listCardContent}>
                        <Text variant="bodyMedium" style={styles.listName}>{c.fullName}</Text>
                        <Text variant="bodySmall" style={styles.label}>{c.phone}</Text>
                      </Card.Content>
                    </Card>
                  </TouchableOpacity>
                ))}
                <TouchableOpacity style={styles.addCustomerRow} onPress={handleAddCustomer} activeOpacity={0.7}>
                  <MaterialCommunityIcons name="account-plus-outline" size={20} color={colors.primary} />
                  <Text style={styles.addCustomerRowText}>Add New Customer</Text>
                </TouchableOpacity>
              </>
            )}
          </>
        )}
      </ScrollView>

      <View style={styles.bottomBar}>
        <Button mode="contained" onPress={handleNext} disabled={!selectedCustomer || !storeId}
          style={styles.bottomButton} buttonColor={colors.primary}>
          {t('common.next')}
        </Button>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  content: {flex: 1, padding: spacing.md},
  stepTitle: {fontWeight: 'bold', color: colors.text, marginBottom: spacing.md},
  storeRow: {
    flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.md,
  },
  storeChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
  storeChipActive: {backgroundColor: colors.primary, borderColor: colors.primary},
  storeChipText: {color: colors.text, fontSize: 13},
  storeChipTextActive: {color: colors.white, fontSize: 13},
  storeDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    marginBottom: spacing.md,
    gap: spacing.sm,
  },
  storeDropdownText: {
    flex: 1,
    fontSize: 15,
    color: colors.textSecondary,
  },
  storeDropdownTextSelected: {
    color: colors.text,
    fontWeight: '500',
  },
  storeModal: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.lg,
    borderRadius: 12,
    maxHeight: '60%',
    overflow: 'hidden',
  },
  storeModalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    padding: spacing.md,
  },
  storeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
    paddingRight: spacing.md,
  },
  storeOptionContent: {
    flex: 1,
  },
  storeOptionName: {
    fontSize: 15,
    color: colors.text,
  },
  storeOptionCity: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  searchbar: {marginBottom: spacing.sm, backgroundColor: colors.surface, elevation: 0},
  selectedCard: {marginBottom: spacing.md, backgroundColor: colors.surface},
  selectedRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'},
  listCard: {marginBottom: spacing.xs, backgroundColor: colors.surface},
  listCardContent: {paddingVertical: spacing.xs},
  listName: {color: colors.text},
  label: {color: colors.textSecondary},
  noResultsContainer: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.md,
  },
  noResultsText: {
    color: colors.textSecondary,
    fontSize: 14,
    textAlign: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
  },
  addCustomerButton: {
    borderRadius: 8,
  },
  addCustomerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  addCustomerRowText: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  bottomBar: {
    flexDirection: 'row', padding: spacing.md, backgroundColor: colors.surface,
    gap: spacing.sm, elevation: 4,
  },
  bottomButton: {flex: 1, borderRadius: 8},
});

export default OrderCreateScreen;
